import { Component, ViewChild } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { Pagination, TenantPermissionAssigneeTargetService, TenantRoleService, TenantTeamRoleService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../../modules/permission/facades/permission.facade';
import { TeamRoleModel } from '../../../../../../models/team-permissions.model';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../listing.utils';
import { PaginationComponent } from '../../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../../view-column.component';
import { AppConfig } from '../../../../../../visa';
import { forkJoin, iif, Observable, of, zip } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../../permission-controller/root';
import { TeamPermActions } from '../../../../team-permissions-controller/actions';
import { TeamPermSelectors } from '../../../../team-permissions-controller/selectors';
import { TeamPermStates } from '../../../../team-permissions-controller/states';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-team-permission-role',
  templateUrl: './team-permission-role.component.html',
  styleUrls: ['./team-permission-role.component.scss']
})
export class TeamPermissionRoleComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;

  protected readonly index = 1;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  // api visa
  apiVisa = AppConfig.apiVisa;

  searchValue: any;

  // initial ag grid state
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'single';

  rowData = [
    // {
    //   role: 'Can issue sales for southern branches',
    //   num_permissions: 3
    // },
    // {
    //   role: 'Can view report from Branch A',
    //   num_permissions: 1
    // },
    // {
    //   role: 'Can view P&L for Company B',
    //   num_permissions: 5
    // },
  ]

  teamGuid: string;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  constructor(
    private tenantRoleService: TenantRoleService,
    private tenantTeamRoleService: TenantTeamRoleService,
    private permAssigneeTargetService: TenantPermissionAssigneeTargetService,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
   ngOnInit() {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });

    this.columnsDefs = [
      {
        headerName: 'Role Code',
        field: 'app_mst_role.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'Role Name',
        field: 'app_mst_role.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'No. of Permissions',
        field: 'num_permissions',
        headerClass: 'text-right',
        cellStyle: {textAlign: 'right'}
      }
    ];
  }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    this.subSink.sink = this.store.select(TeamPermSelectors.selectGuid).subscribe(guid => {
      if (guid) {
        this.teamGuid = guid;
        const datasource = this.getDatasource();
        this.gridApi.setServerSideDatasource(datasource);
      }
    });

    // update ag grid every time there is an update
    this.subSink.sink = this.store.select(TeamPermSelectors.selectUpdateRoleListing).subscribe(x => {
      if (x) {
        this.gridApi.refreshServerSideStore();
        // set back to false
        this.store.dispatch(TeamPermActions.setUpdateRoleListing({update: false}));
      }
    })
  }

  /**
   * Returns the datasource for the AG grid.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns the datasource
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;
    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'mst_grp_guid', operator: '=', value: this.teamGuid}
    ]
    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    return {
      getRows: grid => {

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, criteria);

        this.subSink.sink = this.tenantTeamRoleService.getByCriteria(pagination, this.apiVisa)
          .pipe(
            mergeMap(a => {
              const source: Observable<TeamRoleModel>[] = [];
              a.data.forEach((b) => {
                const newModel: TeamRoleModel = new TeamRoleModel();
                newModel.link_grp_to_role = b.app_mst_link_grp_to_role;

                const role$ = this.getRole(b.app_mst_link_grp_to_role.mst_role_guid.toString());
                const perms$ = this.getPermissionAssigneeTargets(b.app_mst_link_grp_to_role.mst_role_guid.toString());

                const result$ = zip(role$, perms$)
                  .pipe(
                    map(([role, perms]) => {
                      newModel.app_mst_role = role.data.app_mst_role;
                      newModel.num_permissions = perms.totalRecords;

                      return newModel
                    })
                  )

                source.push(result$);
              });
              return iif(() => source.length > 0,
              forkJoin(source).pipe(map(x => {
                return {...a,
                data: x}
              })),
              of({...a, data: [], totalRecords: 0}))
            })
          ).subscribe(a => {
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

            grid.successCallback(data, totalRecords);
          }, err => {
            grid.failCallback();
          })

      }
    }

  }

  /**
   * Get the permission assignee targets under this role.
   *
   * @param roleGuid : the guid of the role
   */
  getPermissionAssigneeTargets(roleGuid: string) {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'assignee_hdr_table', operator: '=', value: 'app_mst_role'},
      {columnName: 'assignee_hdr_guid', operator: '=', value: roleGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.permAssigneeTargetService.getByCriteria(pagination, this.apiVisa)
      .pipe()
  }

  /**
   * This function is called every time the user clicks on a row in the AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {

    // save entity in store
    this.store.dispatch(TeamPermActions.selectTeamRole({teamRole: entity}));

    // deactivate add, return and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateReturn: true,
          deactivateAdd: true,
          deactivateList: true
        });
      // trigger the edit permissions template view column
      this.viewColFacade.onNext(4);
    }
  }

  getRole(roleGuid: string) {
    return this.tenantRoleService.getByGuid(roleGuid, this.apiVisa)
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_mst_link_grp_to_role link
   *   LEFT JOIN app_mst_role hdr ON link.mst_role_guid = hdr.guid
   *   WHERE (hdr.name ILIKE '%x%' OR hdr.code ILIKE '%x%')
   *     AND link.mst_grp_guid = '<team>' AND link.status != 'DELETED'
   *
   * GET iam/team-role-links/team-to-role/query -> LinkGroupToRoleUow ->
   * generateWhereClauseByCriteria joins app_mst_role itself and applies
   * `search_word` as a bound ILIKE over the role's code/name (and the group's
   * code/name), with `status != 'DELETED'` as its default. The `mst_grp_guid`
   * scoping stays in this component's default criteria, which getDatasource
   * concatenates with what is passed in.
   *
   * Behaviour note: the keyword also matches the linked team's code and name.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Reset the search bar.
   */
  reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
